// BrandProfileFormFull.jsx — FIXED
// Fixes:
//   ✅ INFINITE LOOP: useSelector with shallowEqual, no dispatch inside effects
//   ✅ localStorage write debounced via ref — never triggers re-renders
//   ✅ setNestedField uses structuredClone + thunk pattern (reads fresh state)
//   ✅ handleMultiSelectChange stabilized with thunk pattern
//   ✅ All brand fields present with correct character limits
//   ✅ Services array fix preserved
//   ✅ Logo File ref fix preserved

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import SignupNav from "../components/SignupNav";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Checkbox,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Button,
  Typography,
  IconButton,
  Tooltip,
  LinearProgress,
  Collapse,
} from "@mui/material";
import { SketchPicker } from "react-color";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { updateBrandProfile, resetForm, hydrate } from "../store/formSlice";
import { loadState } from "../store/localStorage";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  ink: "#0D0D12",
  inkLight: "#2A2A35",
  muted: "#6B6B80",
  border: "#E4E4EF",
  borderFocus: "#5B5BF6",
  surface: "#FAFAFD",
  white: "#FFFFFF",
  accent: "#5B5BF6",
  accentSoft: "#EDEDFF",
  accentGlow: "rgba(91,91,246,0.12)",
  success: "#22C55E",
  warn: "#F59E0B",
  danger: "#EF4444",
  optional: "#8B8BAA",
  optionalBg: "#F6F6FB",
  sectionBg: "#FFFFFF",
  gradTop: "linear-gradient(135deg, #0D0D12 0%, #1A1A2E 100%)",
};

const fontDisplay = "'Clash Display', 'DM Sans', system-ui, sans-serif";
const fontBody = "'DM Sans', 'Instrument Sans', system-ui, sans-serif";
const fontMono = "'JetBrains Mono', 'Fira Code', monospace";

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
    *, *::before, *::after { box-sizing: border-box; }
    body { background: #F4F4FA; font-family: ${fontBody}; color: ${T.ink}; -webkit-font-smoothing: antialiased; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 99px; }
    ::-webkit-scrollbar-thumb:hover { background: ${T.muted}; }
    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes logoGlow { 0%,100% { box-shadow: 0 0 20px rgba(91,91,246,0.3); } 50% { box-shadow: 0 0 32px rgba(91,91,246,0.5); } }
    .field-card { animation: fadeSlideUp 0.35s ease both; }
    .MuiOutlinedInput-root { border-radius: 10px !important; font-family: ${fontBody} !important; font-size: 14px !important; background: ${T.white} !important; transition: box-shadow 0.2s, border-color 0.2s !important; }
    .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color: ${T.accent} !important; }
    .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color: ${T.accent} !important; border-width: 2px !important; }
    .MuiOutlinedInput-root.Mui-focused { box-shadow: 0 0 0 4px ${T.accentGlow} !important; }
    .MuiFormHelperText-root { font-family: ${fontMono} !important; font-size: 11px !important; color: ${T.muted} !important; }
    .MuiMenuItem-root { font-family: ${fontBody} !important; font-size: 13.5px !important; }
    .MuiChip-root { font-family: ${fontBody} !important; font-size: 12.5px !important; font-weight: 500 !important; background: ${T.accentSoft} !important; color: ${T.accent} !important; border: 1px solid rgba(91,91,246,0.25) !important; height: 28px !important; }
    .MuiRadio-root.Mui-checked { color: ${T.accent} !important; }
    .MuiCheckbox-root.Mui-checked { color: ${T.accent} !important; }
    .MuiLinearProgress-bar { background: ${T.accent} !important; }
    .MuiLinearProgress-root { background: ${T.border} !important; border-radius: 99px !important; }
    @media (max-width: 768px) {
      .MuiTab-root { font-size: 12px !important; padding: 6px 10px !important; min-width: unset !important; }
      .MuiTabs-root { max-width: 100vw; }
    }
    @media (max-width: 480px) {
      .MuiTab-root { font-size: 11px !important; padding: 4px 8px !important; }
      .MuiInputBase-input { font-size: 14px !important; }
    }
  `}</style>
);

// ─── UI PRIMITIVES ────────────────────────────────────────────────────────────
const SectionCard = ({ children, index, title, subtitle, icon }) => (
  <Box
    className="field-card"
    sx={{
      background: T.sectionBg,
      borderRadius: "20px",
      border: `1px solid ${T.border}`,
      p: { xs: 3, md: 4 },
      mb: 3,
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "4px",
        height: "100%",
        background: `linear-gradient(180deg, ${T.accent} 0%, rgba(91,91,246,0.2) 100%)`,
        borderRadius: "20px 0 0 20px",
      },
    }}
  >
    {(title || icon) && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        {icon && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: T.accentSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Box>
          {index && (
            <Typography
              sx={{
                fontFamily: fontMono,
                fontSize: "11px",
                color: T.accent,
                fontWeight: 600,
                letterSpacing: "0.08em",
                mb: 0.25,
              }}
            >
              SECTION {String(index).padStart(2, "0")}
            </Typography>
          )}
          {title && (
            <Typography
              sx={{
                fontFamily: fontDisplay,
                fontWeight: 700,
                fontSize: "17px",
                color: T.ink,
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography sx={{ fontSize: "13px", color: T.muted, mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    )}
    {children}
  </Box>
);

const FieldLabel = ({ children, required = true, tooltip }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
    <Typography
      sx={{
        fontWeight: 600,
        fontSize: "13.5px",
        color: T.inkLight,
        fontFamily: fontBody,
      }}
    >
      {children}
    </Typography>
    {!required && (
      <Box
        sx={{
          px: 1,
          py: 0.2,
          background: T.optionalBg,
          border: `1px solid ${T.border}`,
          borderRadius: "6px",
          fontSize: "10.5px",
          fontFamily: fontMono,
          color: T.optional,
          fontWeight: 500,
          letterSpacing: "0.04em",
        }}
      >
        optional
      </Box>
    )}
    {tooltip && (
      <Tooltip title={tooltip} placement="right" arrow>
        <InfoOutlinedIcon
          sx={{ fontSize: 15, color: T.muted, cursor: "help" }}
        />
      </Tooltip>
    )}
  </Box>
);

const FieldError = ({ msg }) =>
  msg ? (
    <Typography
      sx={{
        fontSize: "12px",
        color: T.danger,
        mt: 0.75,
        fontFamily: fontBody,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      ⚠ {msg}
    </Typography>
  ) : null;

const StyledSelect = ({ children, error, ...props }) => (
  <FormControl fullWidth error={error}>
    <Select
      sx={{ borderRadius: "10px", background: T.white }}
      MenuProps={{
        PaperProps: {
          sx: {
            borderRadius: "12px",
            border: `1px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            mt: 0.5,
          },
        },
      }}
      {...props}
    >
      {children}
    </Select>
  </FormControl>
);

const OptionalAccordion = ({ title, badge, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        border: `1.5px dashed ${T.border}`,
        borderRadius: "14px",
        overflow: "hidden",
        mb: 0,
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          cursor: "pointer",
          background: open ? T.accentSoft : T.optionalBg,
          transition: "background 0.2s",
          "&:hover": { background: T.accentSoft },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <LockOpenOutlinedIcon sx={{ fontSize: 16, color: T.accent }} />
          <Typography
            sx={{ fontWeight: 600, fontSize: "13.5px", color: T.inkLight }}
          >
            {title}
          </Typography>
          {badge && (
            <Box
              sx={{
                px: 1,
                py: 0.2,
                background: T.accentSoft,
                border: `1px solid rgba(91,91,246,0.3)`,
                borderRadius: "6px",
                fontSize: "10.5px",
                fontFamily: fontMono,
                color: T.accent,
                fontWeight: 600,
              }}
            >
              {badge}
            </Box>
          )}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ color: T.muted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: T.muted }} />
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 3, pb: 3, pt: 2, borderTop: `1px solid ${T.border}` }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

function ZenithpostLogo() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          background: "linear-gradient(135deg, #5B5BF6 0%, #A378F8 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: fontDisplay,
          fontWeight: 800,
          fontSize: 15,
          color: "white",
          flexShrink: 0,
          animation: "logoGlow 3s ease infinite",
          boxShadow: "0 4px 12px rgba(91,91,246,0.35)",
        }}
      >
        Z
      </Box>
      <Typography
        sx={{
          fontFamily: fontDisplay,
          fontWeight: 700,
          fontSize: 15,
          color: T.ink,
          lineHeight: 1,
        }}
      >
        zenithpost<span style={{ color: T.accent }}>.ai</span>
      </Typography>
    </Box>
  );
}

// ─── DATA ─────────────────────────────────────────────────────────────────────
const industriesOptions = [
  "Pet Care",
  "Healthcare",
  "AI / Technology",
  "Consumer Wellness",
  "E-commerce",
  "Education",
  "Finance",
  "Media",
  "Retail",
  "Other",
  "Food & Beverage",
  "Travel & Tourism",
  "Automotive",
  "Fashion & Apparel",
  "Fitness & Sports",
  "Beauty & Cosmetics",
  "Home & Garden",
  "Telecommunications",
  "Software / SaaS",
  "Entertainment",
  "Gaming",
  "Real Estate",
  "Logistics & Transportation",
  "Non-profit / NGOs",
  "Hospitality",
  "Pharmaceuticals",
  "Insurance",
  "Legal Services",
  "Marketing & Advertising",
  "Energy & Utilities",
  "Manufacturing",
  "Publishing",
  "Event Management",
  "Consumer Electronics",
];
const targetAudienceOptions = [
  "First-time customers",
  "Existing customers",
  "Families",
  "Professionals",
  "Enthusiasts / Hobbyists",
  "Emotionally driven buyers",
  "Data-driven buyers",
  "B2B Clients",
  "Other",
  "Millennials",
  "Gen Z",
  "Baby Boomers",
  "High-income earners",
  "Middle-income earners",
  "Students",
  "Entrepreneurs / Startups",
  "Small business owners",
  "Corporate executives",
  "Frequent travelers",
  "Eco-conscious buyers",
  "Tech-savvy users",
  "Health-conscious users",
  "Luxury consumers",
  "Budget-conscious consumers",
  "Parents",
  "Pet owners",
  "Urban residents",
  "Suburban residents",
  "Remote workers",
  "Creatives / Artists",
  "Investors / Traders",
  "Community organizers",
  "Pet Owners",
];
const brandValuesOptions = [
  "Trust",
  "Empathy",
  "Innovation",
  "Transparency",
  "Simplicity",
  "Authority",
  "Care",
  "Creativity",
  "Sustainability",
  "Other",
  "Responsibility",
  "Authenticity",
  "Collaboration",
  "Diversity & Inclusion",
  "Excellence",
  "Courage",
  "Reliability",
  "Passion",
  "Commitment",
  "Customer-centricity",
  "Flexibility",
  "Accountability",
  "Fun",
  "Innovation-driven",
  "Quality",
  "Growth",
  "Leadership",
  "Integrity",
  "Adventure",
  "Inspiration",
];
const brandPersonalityOptions = [
  "Warm",
  "Playful",
  "Calm",
  "Professional",
  "Bold",
  "Reflective",
  "Friendly",
  "Curious",
  "Adventurous",
  "Innovative",
  "Other",
  "Energetic",
  "Charismatic",
  "Sophisticated",
  "Reliable",
  "Passionate",
  "Creative",
  "Humorous",
  "Inclusive",
  "Optimistic",
  "Confident",
  "Patient",
  "Grounded",
  "Dynamic",
  "Authentic",
  "Empathetic",
  "Visionary",
  "Inspirational",
  "Strategic",
  "Approachable",
];
const writingVoiceOptions = [
  "Friendly guide",
  "Experienced mentor",
  "Calm authority",
  "Curious storyteller",
  "Professional expert",
  "Conversational",
  "Inspirational",
  "Authoritative",
  "Other",
  "Humorous",
  "Empathetic",
  "Persuasive",
  "Educational",
  "Engaging",
  "Concise",
  "Formal",
  "Informal",
  "Narrative",
  "Technical",
  "Analytical",
  "Motivational",
  "Encouraging",
  "Inclusive",
  "Thought-provoking",
  "Direct",
  "Reflective",
  "Optimistic",
  "Story-driven",
  "Visionary",
];
const brandArchetypeOptions = [
  "The Caregiver",
  "The Guide",
  "The Hero",
  "The Creator",
  "The Explorer",
  "The Lover",
  "The Jester",
  "The Sage",
  "The Innocent",
  "Other",
  "The Ruler",
  "The Magician",
  "The Everyman",
  "The Rebel",
  "The Visionary",
  "The Innovator",
  "The Protector",
  "The Connector",
  "The Pioneer",
  "The Advocate",
  "The Storyteller",
  "The Strategist",
  "The Builder",
  "The Mentor",
  "The Entertainer",
  "The Designer",
  "The Philosopher",
  "The Champion",
  "The Leader",
  "The Altruist",
];
const avoidContentOptions = [
  "Fear-based language",
  "Aggressive sales tactics",
  "Hype / exaggeration",
  "Medical or legal guarantees",
  "False urgency",
  "Clickbait headlines",
  "Misleading claims",
  "Negative competitor comparisons",
  "Political statements",
  "Religious messaging",
  "Controversial social issues",
  "Profanity",
  "Slang or overly casual tone",
  "Corporate jargon",
  "Buzzwords without substance",
  "Overly technical language",
  "Complex legal disclaimers",
  "Hard-selling CTAs",
  "Emotional manipulation",
  "Scarcity pressure tactics",
  "Guilt-based messaging",
  "Body shaming language",
  "Stereotyping",
  "Cultural insensitivity",
  "Gender bias",
  "Unrealistic results",
  "Fake testimonials",
  "Overpromising outcomes",
  "Humor about sensitive topics",
  "Excessive emojis",
  "Other",
];
const visualStyleOptions = [
  "Soft daylight",
  "Cinematic",
  "Editorial photography",
  "Minimalist",
  "Lifestyle",
  "Studio-style",
  "Other",
  "Vintage",
  "Rustic",
  "Bold colors",
  "Monochrome",
  "High contrast",
  "Flat design",
  "Illustrative",
  "3D renders",
  "Abstract",
  "Playful",
  "Luxury / Premium",
  "Muted tones",
  "Nature-inspired",
  "Geometric",
  "Textured",
  "Hand-drawn",
  "Animated",
  "Collage",
  "Surreal",
  "Pop art",
  "Clean & modern",
  "Futuristic",
  "Artistic",
];
const positioningStyleOptions = [
  "Premium",
  "Friendly & Accessible",
  "Science-backed",
  "Lifestyle-oriented",
  "Other",
  "Affordable",
  "Luxury",
  "Innovative",
  "Trustworthy",
  "Convenient",
  "Exclusive",
  "Eco-friendly",
  "Community-focused",
  "Data-driven",
  "Fast & Reliable",
  "Expert-driven",
  "Customer-first",
  "Emotional appeal",
  "Practical",
  "Authentic",
  "High-tech",
  "Cutting-edge",
  "Minimalist",
  "Versatile",
  "Comfortable",
  "Playful",
  "Youthful",
  "Professional",
  "Inspirational",
  "Strategic",
];
const platformToneOptions = {
  instagram: [
    "Emotional & intimate",
    "Light & playful",
    "Calm & reflective",
    "Trendy",
    "Inspirational",
    "Vibrant & colorful",
    "Authentic",
    "Humorous",
    "Minimalist",
    "Luxury-focused",
    "Casual",
    "Artistic",
    "Youthful",
    "Story-driven",
    "Bold & daring",
    "Community-focused",
    "Emotional storytelling",
    "Motivational",
    "Adventure-focused",
    "Creative visuals",
    "Whimsical",
    "Informative",
    "Heartfelt",
    "Engaging",
    "Visionary",
    "Professional",
    "Cinematic",
    "Tech-focused",
    "Eco-conscious",
    "Inclusive",
    "Other",
  ],
  facebook: [
    "Community-focused",
    "Story-driven",
    "Informative",
    "Engaging",
    "Inspirational",
    "Friendly & supportive",
    "Educational",
    "Casual",
    "Authoritative",
    "Professional",
    "Motivational",
    "Humorous",
    "Emotional",
    "Data-driven",
    "Reliable",
    "Event-focused",
    "Conversational",
    "Inclusive",
    "Trustworthy",
    "Creative",
    "Optimistic",
    "Helpful",
    "Visionary",
    "Local-focused",
    "Lifestyle-oriented",
    "Entertainment-driven",
    "Interactive",
    "News-style",
    "Socially conscious",
    "Customer-centric",
    "Other",
  ],
  linkedIn: [
    "Thought leadership",
    "Insight-driven",
    "Educational",
    "Professional",
    "Authoritative",
    "Expert advice",
    "Data-driven",
    "Analytical",
    "Career-focused",
    "Networking-focused",
    "Engaging",
    "Motivational",
    "Strategic",
    "Innovative",
    "Trustworthy",
    "Informative",
    "Executive-focused",
    "B2B-focused",
    "Industry insights",
    "Leadership",
    "Research-based",
    "Problem-solving",
    "Analytical storytelling",
    "Results-driven",
    "Visionary",
    "Professional guidance",
    "Expert commentary",
    "Strategic insights",
    "Ethical",
    "Inclusive",
    "Other",
  ],
  newsletter: [
    "Conversational",
    "Calm narration",
    "Documentary-style",
    "Storytelling",
    "Dynamic & engaging",
    "Humorous",
    "Emotional",
    "Inspirational",
    "Educational",
    "Professional",
    "Informative",
    "Playful",
    "Energetic",
    "Authentic",
    "Minimalist",
    "Cinematic",
    "Luxury feel",
    "Casual",
    "Heartfelt",
    "Action-oriented",
    "Adventure-focused",
    "Tech-driven",
    "Creative visuals",
    "Community-focused",
    "Motivational",
    "Interactive",
    "Empowering",
    "Data-driven",
    "Thought-provoking",
    "Relaxed & friendly",
    "Other",
  ],
  twitter: [
    "Conversational",
    "Calm narration",
    "Documentary-style",
    "Storytelling",
    "Dynamic & engaging",
    "Humorous",
    "Emotional",
    "Inspirational",
    "Educational",
    "Professional",
    "Informative",
    "Playful",
    "Energetic",
    "Authentic",
    "Minimalist",
    "Cinematic",
    "Luxury feel",
    "Casual",
    "Heartfelt",
    "Action-oriented",
    "Adventure-focused",
    "Tech-driven",
    "Creative visuals",
    "Community-focused",
    "Motivational",
    "Interactive",
    "Empowering",
    "Data-driven",
    "Thought-provoking",
    "Relaxed & friendly",
    "Other",
  ],
  blog: [
    "Conversational",
    "Calm narration",
    "Documentary-style",
    "Storytelling",
    "Dynamic & engaging",
    "Humorous",
    "Emotional",
    "Inspirational",
    "Educational",
    "Professional",
    "Informative",
    "Playful",
    "Energetic",
    "Authentic",
    "Minimalist",
    "Cinematic",
    "Luxury feel",
    "Casual",
    "Heartfelt",
    "Action-oriented",
    "Adventure-focused",
    "Tech-driven",
    "Creative visuals",
    "Community-focused",
    "Motivational",
    "Interactive",
    "Empowering",
    "Data-driven",
    "Thought-provoking",
    "Relaxed & friendly",
    "Other",
  ],
};
const emotionalThemeOptions = [
  "Trust",
  "Reassurance",
  "Growth",
  "Curiosity",
  "Confidence",
  "Happiness",
  "Excitement",
  "Empathy",
  "Hope",
  "Inspiration",
  "Motivation",
  "Joy",
  "Security",
  "Adventure",
  "Luxury",
  "Professionalism",
  "Innovation",
  "Creativity",
  "Authenticity",
  "Passion",
  "Community",
  "Courage",
  "Optimism",
  "Comfort",
  "Serenity",
  "Wisdom",
  "Fun",
  "Love",
  "Respect",
  "Empowerment",
];
const seasonalContextOptions = [
  "Evergreen",
  "Holiday",
  "Back-to-work",
  "Product launch",
  "Black Friday / Cyber Monday",
  "Christmas",
  "New Year",
  "Valentine's Day",
  "Easter",
  "Summer Sale",
  "Back-to-school",
  "Spring Sale",
  "Autumn Sale",
  "Halloween",
  "Mother's Day",
  "Father's Day",
  "Independence Day",
  "Thanksgiving",
  "Graduation",
  "Seasonal Campaign",
  "Anniversary",
  "Flash Sale",
  "Limited Edition",
  "Weekly Special",
  "Winter Sale",
  "Quarterly Promotion",
  "End-of-season",
  "Special Event",
  "Charity Campaign",
  "Awareness Week",
];
const funnelStageOptions = [
  "Mostly Cold — never heard of us",
  "Mostly Warm — aware but not committed",
  "Mostly Hot — ready to buy",
  "Mixed — roughly equal cold & warm",
  "Mixed — roughly equal warm & hot",
  "Mixed — all three stages",
];

// ─── CHAR LIMITS ──────────────────────────────────────────────────────────────
const MAX_CHARS = 50;
const BIG_MAX_CHARS = 400;
const VOICE_MAX_CHARS = 800;

// ─── REQUIRED FIELDS (used for progress bar calc) ────────────────────────────
const REQUIRED_FIELDS = [
  "brandName",
  "companyWebsite",
  "industries",
  "targetAudiences",
  "brandArchetype",
  "brandValues",
  "brandPersonality",
  "writingVoice",
  "avoidContent",
  "visualStyle",
  "positioningStyle",
  "brandMission",
  "valueProposition",
  "problemSolved",
  "competitors",
  "competitiveAdvantage",
  "emotionalToneRange",
  "sentenceComplexity",
  "brandPromise",
  "brandEnemy",
  "coreBeliefs",
];

const MULTI_OTHER_MAP = {
  industries: "otherIndustry",
  targetAudiences: "otherTargetAudience",
  brandValues: "otherBrandValue",
  brandPersonality: "otherBrandPersonality",
  brandArchetype: "otherBrandArchetype",
  writingVoice: "otherWritingVoice",
  avoidContent: "otherAvoidContent",
  visualStyle: "otherVisualStyle",
  positioningStyle: "otherPositioningStyle",
};

// Pure helpers — defined outside component so they never cause re-renders
function calcProgress(profile, hasLogo) {
  const filled = REQUIRED_FIELDS.filter((k) => {
    const v = profile[k];
    return Array.isArray(v) ? v.length > 0 : !!v;
  });
  return Math.round(
    ((filled.length + (hasLogo ? 1 : 0)) / (REQUIRED_FIELDS.length + 1)) * 100,
  );
}

function formatMultiSelect(array, otherValue) {
  if (!array?.length) return "";
  const values = array.filter(Boolean).filter((v) => v !== "Other");
  if (array.includes("Other") && otherValue?.trim())
    values.push(otherValue.trim());
  return values.join(", ");
}

function arrayToString(array) {
  if (!array) return "";
  if (!Array.isArray(array)) return String(array);
  return array.filter((s) => s?.trim()).join(" | ");
}

function servicesStringToArray(val) {
  if (Array.isArray(val)) return val.length ? val : [""];
  if (!val || typeof val !== "string") return [""];
  const sep = val.includes(" | ") ? " | " : ",";
  return val.split(sep).map((s) => s.trim()).filter(Boolean);
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function BrandProfileFormFull() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ✅ FIX: shallowEqual prevents spurious re-renders when Redux returns
  // a new object with identical top-level values
  const brandProfile = useSelector((s) => s.form.brandProfile, shallowEqual);

  const logoFileRef = useRef(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [errors, setErrors] = useState({});
  const [ready, setReady] = useState(false);

  // ✅ FIX: localStorage save via debounced ref — never calls setState or dispatch
  const saveTimerRef = useRef(null);
  const latestProfileRef = useRef(brandProfile);
  latestProfileRef.current = brandProfile;

  useEffect(() => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const { logo, ...rest } = latestProfileRef.current;
      try {
        localStorage.setItem("brandProfile", JSON.stringify(rest));
      } catch (e) {
        console.warn("localStorage save failed:", e);
      }
    }, 300);
    return () => clearTimeout(saveTimerRef.current);
  }, [brandProfile]);

  // Hydrate on mount only — no deps means this runs exactly once
  useEffect(() => {
    const persisted = loadState();
    if (persisted?.form?.brandProfile) dispatch(hydrate(persisted));
    const email = localStorage.getItem("email");
    if (!email?.trim()) {
      navigate("/signup", { replace: true });
      return;
    }
    setReady(true);
    return () => clearTimeout(saveTimerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Progress is pure derived value — no setState, no effect needed
  const progress = calcProgress(brandProfile, hasLogo);

  // ─── Dispatch helpers — all use thunk pattern to read fresh state ──────────
  // This prevents stale closure issues that cause extra dispatches

  const setNestedField = useCallback(
    (path, value) => {
      dispatch((dispatch2, getState) => {
        const current = getState().form.brandProfile;
        const updated = structuredClone(current);
        const keys = path.split(".");
        let node = updated;
        keys.forEach((key, idx) => {
          if (idx === keys.length - 1) node[key] = value;
          else {
            if (!node[key] || typeof node[key] !== "object") node[key] = {};
            node = node[key];
          }
        });
        dispatch2(updateBrandProfile(updated));
      });
    },
    [dispatch],
  );

  const handleMultiSelectChange = useCallback(
    (field, value) => {
      if (value.length > 5) return;
      dispatch((dispatch2, getState) => {
        const current = getState().form.brandProfile;
        const otherKey = MULTI_OTHER_MAP[field];
        const updated = { ...current, [field]: value };
        if (
          otherKey &&
          current[field]?.includes("Other") &&
          !value.includes("Other")
        ) {
          updated[otherKey] = "";
        }
        dispatch2(updateBrandProfile(updated));
      });
    },
    [dispatch],
  );

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    logoFileRef.current = file;
    setHasLogo(true);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  // ─── Services helpers (all use thunk pattern) ─────────────────────────────
  const getServicesFromProfile = (profile) => {
    return servicesStringToArray(profile.services);
  };
  // Sync getter for render — reads from current selector value
  const services = getServicesFromProfile(brandProfile);

  const handleServiceChange = useCallback(
    (idx, value) => {
      dispatch((dispatch2, getState) => {
        const current = getState().form.brandProfile;
        const arr = getServicesFromProfile(current);
        const updated = [...arr];
        updated[idx] = value;
        dispatch2(updateBrandProfile({ ...current, services: updated }));
      });
    },
    [dispatch],
  );

  const addService = useCallback(() => {
    dispatch((dispatch2, getState) => {
      const current = getState().form.brandProfile;
      const arr = getServicesFromProfile(current);
      if (arr.length >= 20) return;
      dispatch2(updateBrandProfile({ ...current, services: [...arr, ""] }));
    });
  }, [dispatch]);

  const removeService = useCallback(
    (idx) => {
      dispatch((dispatch2, getState) => {
        const current = getState().form.brandProfile;
        const arr = getServicesFromProfile(current).filter((_, i) => i !== idx);
        dispatch2(
          updateBrandProfile({ ...current, services: arr.length ? arr : [""] }),
        );
      });
    },
    [dispatch],
  );

  // ─── Validation ────────────────────────────────────────────────────────────
  const validateForm = () => {
    const e = {};
    if (!brandProfile.brandName?.trim()) e.brandName = "Brand Name required";
    if (!brandProfile.companyWebsite?.trim())
      e.companyWebsite = "Company Website required";
    if (!brandProfile.contentLanguage?.trim())
      e.contentLanguage = "Content Language required";
    if (!brandProfile.industries?.length)
      e.industries = "Select at least 1 industry";
    if (!brandProfile.targetAudiences?.length)
      e.targetAudiences = "Select at least 1 audience";
    if (!brandProfile.brandArchetype?.length)
      e.brandArchetype = "Select at least 1 archetype";
    if (!brandProfile.brandValues?.length)
      e.brandValues = "Select at least 1 value";
    if (!brandProfile.brandPersonality?.length)
      e.brandPersonality = "Select at least 1 personality";
    if (!brandProfile.writingVoice?.length)
      e.writingVoice = "Select at least 1 writing voice";
    if (!brandProfile.avoidContent?.length)
      e.avoidContent = "Select at least 1 item";
    if (!brandProfile.visualStyle?.length)
      e.visualStyle = "Select at least 1 style";
    if (!brandProfile.positioningStyle?.length)
      e.positioningStyle = "Select at least 1 style";
    if (!services.some((s) => s.trim())) e.services = "Add at least 1 service";
    if (!brandProfile.brandMission?.trim())
      e.brandMission = "Brand mission required";
    if (!brandProfile.valueProposition?.trim())
      e.valueProposition = "Value proposition required";
    if (!brandProfile.problemSolved?.trim())
      e.problemSolved = "Problem solved required";
    if (!brandProfile.competitors?.trim())
      e.competitors = "Competitors required";
    if (!brandProfile.competitiveAdvantage?.trim())
      e.competitiveAdvantage = "Competitive advantage required";
    if (!brandProfile.emotionalToneRange?.trim())
      e.emotionalToneRange = "Select emotional tone";
    if (!brandProfile.sentenceComplexity?.trim())
      e.sentenceComplexity = "Select sentence complexity";
    if (!brandProfile.colorPalette?.primary?.trim())
      e.colorPalette = "Primary color required";
    if (!logoFileRef.current) e.logo = "Upload a logo";
    [
      "instagram",
      "facebook",
      "linkedIn",
      "twitter",
      "newsletter",
      "blog",
    ].forEach((p) => {
      if (!brandProfile.platformPreferences?.[p]?.trim()) {
        if (!e.platformPreferences) e.platformPreferences = {};
        e.platformPreferences[p] = `Select a tone for ${p}`;
      }
    });
    if (!brandProfile.brandPromise?.trim())
      e.brandPromise = "Brand promise required";
    if (!brandProfile.brandEnemy?.trim()) e.brandEnemy = "Brand enemy required";
    if (!brandProfile.coreBeliefs?.trim())
      e.coreBeliefs = "Core beliefs required";
    // if (!brandProfile.primaryCtaPerPlatform?.trim()) e.primaryCtaPerPlatform = "Primary CTA required";
    // if (!brandProfile.claimsNotAllowed?.trim()) e.claimsNotAllowed = "Hard guardrails required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCheckout = async () => {
    if (!validateForm()) {
      const firstErrorEl = document.querySelector("[data-error='true']");
      if (firstErrorEl)
        firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const email = localStorage.getItem("email");
    const { logo, ...rest } = brandProfile;

    const formattedProfile = {
      brandName: rest.brandName || "",
      companyWebsite: rest.companyWebsite || "",
      contentLanguage: rest.contentLanguage || "English",
      industries: formatMultiSelect(rest.industries, rest.otherIndustry),
      targetAudiences: formatMultiSelect(
        rest.targetAudiences,
        rest.otherTargetAudience,
      ),
      brandValues: formatMultiSelect(rest.brandValues, rest.otherBrandValue),
      brandPersonality: formatMultiSelect(
        rest.brandPersonality,
        rest.otherBrandPersonality,
      ),
      writingVoice: formatMultiSelect(
        rest.writingVoice,
        rest.otherWritingVoice,
      ),
      avoidContent: formatMultiSelect(
        rest.avoidContent,
        rest.otherAvoidContent,
      ),
      visualStyle: formatMultiSelect(rest.visualStyle, rest.otherVisualStyle),
      brandArchetype: formatMultiSelect(
        rest.brandArchetype,
        rest.otherBrandArchetype,
      ),
      services: arrayToString(services),
      brandMission: rest.brandMission || "",
      valueProposition: rest.valueProposition || "",
      problemSolved: rest.problemSolved || "",
      competitors: rest.competitors || "",
      competitiveAdvantage: rest.competitiveAdvantage || "",
      emotionalToneRange: rest.emotionalToneRange || "",
      sentenceComplexity: rest.sentenceComplexity || "",
      positioningStyle: formatMultiSelect(
        rest.positioningStyle,
        rest.otherPositioningStyle,
      ),
      colorPalette: {
        primary: rest.colorPalette?.primary || "",
        secondary: rest.colorPalette?.secondary || "",
        background: rest.colorPalette?.background || "",
        accent: rest.colorPalette?.accent || "",
      },
      logoPlacement: rest.logoPlacement || "Bottom-right",
      platformPreferences: {
        instagram:
          rest.platformPreferences?.instagram === "Other"
            ? (rest.platformPreferencesOther?.instagram || "").trim()
            : rest.platformPreferences?.instagram || "",
        facebook:
          rest.platformPreferences?.facebook === "Other"
            ? (rest.platformPreferencesOther?.facebook || "").trim()
            : rest.platformPreferences?.facebook || "",
        linkedIn:
          rest.platformPreferences?.linkedIn === "Other"
            ? (rest.platformPreferencesOther?.linkedIn || "").trim()
            : rest.platformPreferences?.linkedIn || "",
        twitter:
          rest.platformPreferences?.twitter === "Other"
            ? (rest.platformPreferencesOther?.twitter || "").trim()
            : rest.platformPreferences?.twitter || "",
        newsletter:
          rest.platformPreferences?.newsletter === "Other"
            ? (rest.platformPreferencesOther?.newsletter || "").trim()
            : rest.platformPreferences?.newsletter || "",
        blog:
          rest.platformPreferences?.blog === "Other"
            ? (rest.platformPreferencesOther?.blog || "").trim()
            : rest.platformPreferences?.blog || "",
      },
      campaignContext: {
        currentCampaignName: rest.campaignContext?.currentCampaignName || "",
        primaryEmotionalTheme:
          rest.campaignContext?.primaryEmotionalTheme || "",
        seasonalContext: rest.campaignContext?.seasonalContext || "",
      },
      brandPromise: rest.brandPromise || "",
      brandEnemy: rest.brandEnemy || "",
      coreBeliefs: rest.coreBeliefs || "",
      voiceReferenceExamples: rest.voiceReferenceExamples || "",
      exactAudienceLanguage: rest.exactAudienceLanguage || "",
      signaturePhrases: rest.signaturePhrases || "",
      audienceFears: rest.audienceFears || "",
      customerTransformationStory: rest.customerTransformationStory || "",
      socialProofData: rest.socialProofData || "",
      primaryCtaPerPlatform: rest.primaryCtaPerPlatform || "",
      funnelStageFocus: rest.funnelStageFocus || "",
      funnelStageBreakdown: rest.funnelStageBreakdown || "",
      claimsNotAllowed: rest.claimsNotAllowed || "",
    };

    // Store logo as base64 in sessionStorage — CheckoutSuccess uploads after payment
    if (logoFileRef.current && logoPreview) {
      try {
        sessionStorage.setItem("pending_logo_datauri", logoPreview);
        sessionStorage.setItem(
          "pending_logo_mimetype",
          logoFileRef.current.type,
        );
      } catch {
        sessionStorage.setItem("pending_logo_too_large", "1");
      }
    }

    localStorage.setItem("checkout_profile", JSON.stringify(formattedProfile));
    navigate("/checkout");
  };

  // ─── Field renderers ───────────────────────────────────────────────────────
  const renderField = (
    label,
    path,
    multiline = false,
    rows = 1,
    maxChars = MAX_CHARS,
    required = true,
    tooltip = null,
  ) => {
    const value = path.includes(".")
      ? path.split(".").reduce((o, k) => o?.[k] ?? "", brandProfile)
      : brandProfile[path] || "";
    const errorKey = path.includes(".") ? path.split(".").pop() : path;
    return (
      <Box data-error={!!errors[errorKey] ? "true" : "false"}>
        <FieldLabel required={required} tooltip={tooltip}>
          {label}
        </FieldLabel>
        <TextField
          fullWidth
          multiline={multiline}
          rows={rows}
          inputProps={{ maxLength: maxChars }}
          value={value}
          onChange={(e) => setNestedField(path, e.target.value)}
          helperText={`${(value || "").length} / ${maxChars}`}
          error={!!errors[errorKey]}
        />
        <FieldError msg={errors[errorKey]} />
      </Box>
    );
  };

  const renderMultiSelect = (
    label,
    field,
    options,
    required = true,
    tooltip = null,
  ) => (
    <Box data-error={!!errors[field] ? "true" : "false"}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <FieldLabel required={required} tooltip={tooltip}>
          {label} — up to 5
        </FieldLabel>
        {(brandProfile[field] || []).length > 0 && (
          <Typography
            component="span"
            onClick={() => handleMultiSelectChange(field, [])}
            sx={{ fontSize: 12, color: T.accent, cursor: "pointer", userSelect: "none", mb: 0.5 }}
          >
            Clear
          </Typography>
        )}
      </Box>
      <StyledSelect
        multiple
        value={brandProfile[field] || []}
        onChange={(e) => handleMultiSelectChange(field, e.target.value)}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, py: 0.5 }}>
            {selected.map((v) => (
              <Chip key={v} label={v} size="small" />
            ))}
          </Box>
        )}
        error={!!errors[field]}
      >
        {options.map((opt) => (
          <MenuItem
            key={opt}
            value={opt}
            disabled={
              brandProfile[field]?.length >= 5 &&
              !brandProfile[field]?.includes(opt)
            }
          >
            <Checkbox
              checked={(brandProfile[field] || []).includes(opt)}
              size="small"
            />
            <ListItemText primary={opt} />
          </MenuItem>
        ))}
      </StyledSelect>
      <FieldError msg={errors[field]} />
    </Box>
  );

  const renderStrategicField = (
    label,
    fieldKey,
    tooltip,
    rows,
    maxChars,
    required = true,
  ) => {
    const value = brandProfile[fieldKey] || "";
    return (
      <Box data-error={!!errors[fieldKey] ? "true" : "false"}>
        <FieldLabel required={required} tooltip={tooltip}>
          {label}
        </FieldLabel>
        <TextField
          fullWidth
          multiline
          rows={rows}
          inputProps={{ maxLength: maxChars }}
          value={value}
          onChange={(e) => setNestedField(fieldKey, e.target.value)}
          helperText={`${value.length} / ${maxChars}`}
          error={!!errors[fieldKey]}
          placeholder={
            !required
              ? "Skip if you're just starting out — you can add this later."
              : undefined
          }
        />
        <FieldError msg={errors[fieldKey]} />
      </Box>
    );
  };

  if (!ready) return null;

  return (
    <>
      <GlobalStyles />
      <SignupNav title="Brand Profile" backPath="/signup" />

      {/* Sticky progress bar */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: T.white,
          borderBottom: `1px solid ${T.border}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Box sx={{ maxWidth: 1000, mx: "auto" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.75,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ZenithpostLogo />
              <Typography
                sx={{
                  fontSize: "11.5px",
                  fontFamily: fontMono,
                  color: T.muted,
                  fontWeight: 500,
                  display: { xs: "none", sm: "block" },
                }}
              >
                Profile completion
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {progress === 100 && (
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 14, color: T.success }}
                />
              )}
              <Typography
                sx={{
                  fontSize: "11.5px",
                  fontFamily: fontMono,
                  color: progress === 100 ? T.success : T.accent,
                  fontWeight: 700,
                }}
              >
                {progress}%
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 4, borderRadius: 99 }}
          />
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: "auto", pb: 8 }}>
        {/* Page header */}
        <Box
          sx={{
            background: T.gradTop,
            borderRadius: "24px",
            p: { xs: 3, md: 5 },
            mb: 3,
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(ellipse at 80% 50%, rgba(91,91,246,0.3) 0%, transparent 60%)",
              pointerEvents: "none",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: fontMono,
              fontSize: "11px",
              color: "rgba(255,255,255,0.5)",
              fontWeight: 500,
              letterSpacing: "0.1em",
              mb: 1.5,
            }}
          >
            BRAND PROFILE SETUP
          </Typography>
          <Typography
            sx={{
              fontFamily: fontDisplay,
              fontWeight: 700,
              fontSize: { xs: "26px", md: "32px" },
              color: T.white,
              lineHeight: 1.15,
              mb: 1.5,
            }}
          >
            Build your AI brand brain.
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "rgba(255,255,255,0.6)",
              maxWidth: 520,
              lineHeight: 1.7,
            }}
          >
            Every field you fill in trains your content agents to write sharper
            copy, tighter hooks, and content that actually sounds like you.
          </Typography>
        </Box>

        {/* SECTION 1 — BASICS */}
        <SectionCard
          index={1}
          title="Company Basics"
          subtitle="The foundation every agent builds on."
          icon="🏢"
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2.5,
            }}
          >
            {renderField("Brand Name", "brandName", false, 1, MAX_CHARS)}
            {renderField(
              "Company Website",
              "companyWebsite",
              false,
              1,
              MAX_CHARS,
            )}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            {renderMultiSelect("Industries", "industries", industriesOptions)}
            {brandProfile.industries?.includes("Other") && (
              <Box sx={{ mt: 2 }}>
                {renderField(
                  "Specify Industry",
                  "otherIndustry",
                  false,
                  1,
                  MAX_CHARS,
                  false,
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            {renderMultiSelect(
              "Target Audiences",
              "targetAudiences",
              targetAudienceOptions,
            )}
            {brandProfile.targetAudiences?.includes("Other") && (
              <Box sx={{ mt: 2 }}>
                {renderField(
                  "Specify Audience",
                  "otherTargetAudience",
                  false,
                  1,
                  MAX_CHARS,
                  false,
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <FieldLabel>Content Language</FieldLabel>
            <StyledSelect
              value={brandProfile.contentLanguage || "English"}
              onChange={(e) =>
                setNestedField("contentLanguage", e.target.value)
              }
            >
              <MenuItem value="English">English</MenuItem>
            </StyledSelect>
          </Box>
        </SectionCard>

        {/* SECTION 2 — MISSION */}
        <SectionCard
          index={2}
          title="Mission & Identity"
          subtitle="Define who you are, what you believe, and how you show up."
          icon="🎯"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {renderField(
              "Brand Mission",
              "brandMission",
              true,
              3,
              BIG_MAX_CHARS,
            )}
            {renderMultiSelect(
              "Brand Archetype",
              "brandArchetype",
              brandArchetypeOptions,
            )}
            {brandProfile.brandArchetype?.includes("Other") &&
              renderField(
                "Specify Archetype",
                "otherBrandArchetype",
                false,
                1,
                MAX_CHARS,
                false,
              )}
            {renderMultiSelect(
              "Brand Values",
              "brandValues",
              brandValuesOptions,
            )}
            {brandProfile.brandValues?.includes("Other") &&
              renderField(
                "Specify Value",
                "otherBrandValue",
                false,
                1,
                MAX_CHARS,
                false,
              )}
            {renderMultiSelect(
              "Brand Personality",
              "brandPersonality",
              brandPersonalityOptions,
            )}
            {brandProfile.brandPersonality?.includes("Other") &&
              renderField(
                "Specify Personality",
                "otherBrandPersonality",
                false,
                1,
                MAX_CHARS,
                false,
              )}
          </Box>
        </SectionCard>

        {/* SECTION 3 — VOICE */}
        <SectionCard
          index={3}
          title="Voice & Tone"
          subtitle="How your brand speaks across every channel."
          icon="🎙"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {renderMultiSelect(
              "Writing Voice",
              "writingVoice",
              writingVoiceOptions,
            )}
            {brandProfile.writingVoice?.includes("Other") &&
              renderField(
                "Specify Voice",
                "otherWritingVoice",
                false,
                1,
                MAX_CHARS,
                false,
              )}
            {renderMultiSelect(
              "Avoid These in Content",
              "avoidContent",
              avoidContentOptions,
            )}
            {brandProfile.avoidContent?.includes("Other") &&
              renderField(
                "Specify What to Avoid",
                "otherAvoidContent",
                false,
                1,
                MAX_CHARS,
                false,
              )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 3,
              }}
            >
              <Box data-error={!!errors.emotionalToneRange ? "true" : "false"}>
                <FieldLabel>Emotional Tone</FieldLabel>
                <RadioGroup
                  value={brandProfile.emotionalToneRange || "Balanced"}
                  onChange={(e) =>
                    setNestedField("emotionalToneRange", e.target.value)
                  }
                >
                  {[
                    ["Calm", "Calm & Subtle"],
                    ["Balanced", "Balanced & Neutral"],
                    ["Energetic", "Energetic & Bold"],
                  ].map(([val, label]) => (
                    <FormControlLabel
                      key={val}
                      value={val}
                      control={<Radio size="small" />}
                      label={
                        <Typography sx={{ fontSize: "13.5px" }}>
                          {label}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
                <FieldError msg={errors.emotionalToneRange} />
              </Box>
              <Box data-error={!!errors.sentenceComplexity ? "true" : "false"}>
                <FieldLabel>Sentence Complexity</FieldLabel>
                <RadioGroup
                  value={brandProfile.sentenceComplexity || "Medium"}
                  onChange={(e) =>
                    setNestedField("sentenceComplexity", e.target.value)
                  }
                >
                  {["Simple", "Medium", "Advanced"].map((v) => (
                    <FormControlLabel
                      key={v}
                      value={v}
                      control={<Radio size="small" />}
                      label={
                        <Typography sx={{ fontSize: "13.5px" }}>{v}</Typography>
                      }
                    />
                  ))}
                </RadioGroup>
                <FieldError msg={errors.sentenceComplexity} />
              </Box>
            </Box>
          </Box>
        </SectionCard>

        {/* SECTION 4 — PRODUCTS */}
        <SectionCard
          index={4}
          title="Products & Positioning"
          subtitle="What you sell and where you stand in the market."
          icon="📦"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box data-error={!!errors.services ? "true" : "false"}>
              <FieldLabel>Products / Services</FieldLabel>
              {services.map((service, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    mb: 1.5,
                    alignItems: "flex-start",
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="e.g. Brand strategy consultation"
                    value={service || ""}
                    inputProps={{ maxLength: 150 }}
                    onChange={(e) => handleServiceChange(idx, e.target.value)}
                    helperText={`${(service || "").length} / 150`}
                  />
                  {services.length > 1 && (
                    <IconButton
                      onClick={() => removeService(idx)}
                      sx={{ color: T.danger, mt: 0.5, flexShrink: 0 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addService}
                size="small"
                sx={{
                  borderRadius: "8px",
                  borderColor: T.border,
                  color: T.inkLight,
                  fontSize: "13px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: T.accent,
                    color: T.accent,
                    background: T.accentSoft,
                  },
                }}
              >
                Add Another
              </Button>
              <FieldError msg={errors.services} />
            </Box>
            {renderField(
              "Primary Value Proposition",
              "valueProposition",
              true,
              2,
              MAX_CHARS,
            )}
            {renderMultiSelect(
              "Positioning Style",
              "positioningStyle",
              positioningStyleOptions,
            )}
            {brandProfile.positioningStyle?.includes("Other") &&
              renderField(
                "Specify Positioning Style",
                "otherPositioningStyle",
                false,
                1,
                MAX_CHARS,
                false,
              )}
            {renderField(
              "Problem Your Product/Service Solves",
              "problemSolved",
              true,
              2,
              BIG_MAX_CHARS,
            )}
            {renderField(
              "Current Competitors",
              "competitors",
              true,
              2,
              BIG_MAX_CHARS,
            )}
            {renderField(
              "Competitive Advantage",
              "competitiveAdvantage",
              true,
              2,
              BIG_MAX_CHARS,
            )}
          </Box>
        </SectionCard>

        {/* SECTION 5 — VISUAL */}
        <SectionCard
          index={5}
          title="Visual Identity"
          subtitle="Colors, style, and how your brand looks."
          icon="🎨"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {renderMultiSelect(
              "Visual Style Preferences",
              "visualStyle",
              visualStyleOptions,
            )}
            {brandProfile.visualStyle?.includes("Other") && (
              <Box>
                <FieldLabel required={false}>Custom Visual Style</FieldLabel>
                <TextField
                  fullWidth
                  placeholder="e.g. Dark moody editorial"
                  value={brandProfile.otherVisualStyle || ""}
                  inputProps={{ maxLength: MAX_CHARS }}
                  onChange={(e) =>
                    setNestedField("otherVisualStyle", e.target.value)
                  }
                  helperText={`${(brandProfile.otherVisualStyle || "").length} / ${MAX_CHARS}`}
                />
              </Box>
            )}

            <Box>
              <FieldLabel>Brand Colors</FieldLabel>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
                  gap: 2,
                }}
              >
                {["primary", "secondary", "background", "accent"].map(
                  (colorKey) => (
                    <Box
                      key={colorKey}
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: T.muted,
                          textTransform: "capitalize",
                          fontFamily: fontMono,
                        }}
                      >
                        {colorKey}
                      </Typography>
                      <SketchPicker
                        color={
                          brandProfile.colorPalette?.[colorKey] || "#ffffff"
                        }
                        onChange={(c) =>
                          setNestedField("colorPalette", {
                            ...(brandProfile.colorPalette || {}),
                            [colorKey]: c.hex,
                          })
                        }
                        styles={{
                          default: {
                            picker: {
                              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                              borderRadius: "12px",
                              width: "100% !important",
                            },
                          },
                        }}
                      />
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "6px",
                            background:
                              brandProfile.colorPalette?.[colorKey] || "#fff",
                            border: `1px solid ${T.border}`,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontFamily: fontMono,
                            color: T.muted,
                          }}
                        >
                          {brandProfile.colorPalette?.[colorKey] || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
              <FieldError msg={errors.colorPalette} />
            </Box>

            <Box data-error={!!errors.logo ? "true" : "false"}>
              <FieldLabel>Brand Logo</FieldLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                {logoPreview && (
                  <Box
                    sx={{
                      p: 1.5,
                      border: `1px solid ${T.border}`,
                      borderRadius: "12px",
                      background: T.surface,
                    }}
                  >
                    <img
                      src={logoPreview}
                      alt="Logo"
                      style={{
                        maxWidth: 120,
                        maxHeight: 80,
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  sx={{
                    borderRadius: "10px",
                    borderColor: errors.logo ? T.danger : T.border,
                    color: errors.logo ? T.danger : T.inkLight,
                    fontSize: "13px",
                    textTransform: "none",
                    px: 2.5,
                    py: 1.25,
                    "&:hover": {
                      borderColor: T.accent,
                      color: T.accent,
                      background: T.accentSoft,
                    },
                  }}
                >
                  {logoPreview ? "Replace Logo" : "Upload Logo (.png, .jpg)"}
                  <input
                    type="file"
                    hidden
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={handleLogoUpload}
                  />
                </Button>
                {logoPreview && (
                  <Typography sx={{ fontSize: "12px", color: T.success }}>
                    ✓ Logo ready to upload after payment
                  </Typography>
                )}
              </Box>
              <FieldError msg={errors.logo} />
            </Box>

            <Box>
              <FieldLabel required={false}>Logo Placement</FieldLabel>
              <RadioGroup
                row
                value={brandProfile.logoPlacement || "Bottom-right"}
                onChange={(e) =>
                  setNestedField("logoPlacement", e.target.value)
                }
              >
                {["Bottom-left", "Bottom-right"].map(
                  (v) => (
                    <FormControlLabel
                      key={v}
                      value={v}
                      control={<Radio size="small" />}
                      label={
                        <Typography sx={{ fontSize: "13px" }}>
                          {v.replace("-", " ")}
                        </Typography>
                      }
                    />
                  ),
                )}
              </RadioGroup>
            </Box>
          </Box>
        </SectionCard>

        {/* SECTION 6 — PLATFORM */}
        <SectionCard
          index={6}
          title="Platform Preferences"
          subtitle="Dial in the tone for each channel separately."
          icon="📱"
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2.5,
            }}
          >
            {Object.entries(platformToneOptions).map(([platform, options]) => {
              const label =
                platform === "linkedIn"
                  ? "LinkedIn"
                  : platform.charAt(0).toUpperCase() + platform.slice(1);
              const isOther =
                brandProfile.platformPreferences?.[platform] === "Other";
              const placeholders = {
                instagram: "e.g. Raw & unfiltered",
                facebook: "e.g. Nostalgic & warm",
                linkedIn: "e.g. Contrarian thought leader",
                newsletter: "e.g. Long-form philosophical",
                twitter: "e.g. Hot takes & quick wit",
                blog: "e.g. Deep-dive investigative",
              };
              return (
                <Box key={platform}>
                  <FieldLabel tooltip={`Tone style for ${label} content`}>
                    {label} Tone
                  </FieldLabel>
                  <StyledSelect
                    value={brandProfile.platformPreferences?.[platform] || ""}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      dispatch((d2, getState) => {
                        const current = getState().form.brandProfile;
                        const updatedPrefs = {
                          ...(current.platformPreferences || {}),
                          [platform]: newVal,
                        };
                        if (newVal !== "Other") {
                          const updatedOther = {
                            ...(current.platformPreferencesOther || {}),
                          };
                          delete updatedOther[platform];
                          d2(
                            updateBrandProfile({
                              ...current,
                              platformPreferences: updatedPrefs,
                              platformPreferencesOther: updatedOther,
                            }),
                          );
                        } else {
                          d2(
                            updateBrandProfile({
                              ...current,
                              platformPreferences: updatedPrefs,
                            }),
                          );
                        }
                      });
                    }}
                    displayEmpty
                    error={!!errors.platformPreferences?.[platform]}
                  >
                    <MenuItem value="" disabled>
                      <Typography sx={{ color: T.muted, fontSize: "13.5px" }}>
                        Select tone…
                      </Typography>
                    </MenuItem>
                    {options.map((opt) => (
                      <MenuItem
                        key={opt}
                        value={opt}
                        sx={
                          opt === "Other"
                            ? {
                                borderTop: `1px solid ${T.border}`,
                                mt: 0.5,
                                pt: 1,
                                fontStyle: "italic",
                                color: T.accent,
                              }
                            : {}
                        }
                      >
                        {opt}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  <FieldError msg={errors.platformPreferences?.[platform]} />
                  {isOther && (
                    <Box sx={{ mt: 1.5 }}>
                      <TextField
                        fullWidth
                        autoFocus
                        placeholder={placeholders[platform]}
                        value={
                          brandProfile.platformPreferencesOther?.[platform] ||
                          ""
                        }
                        inputProps={{ maxLength: 80 }}
                        onChange={(e) =>
                          setNestedField("platformPreferencesOther", {
                            ...(brandProfile.platformPreferencesOther || {}),
                            [platform]: e.target.value,
                          })
                        }
                        helperText={`${(brandProfile.platformPreferencesOther?.[platform] || "").length} / 80`}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            background: T.accentSoft,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: `${T.accent} !important`,
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </SectionCard>

        {/* SECTION 7 — CAMPAIGN */}
        <SectionCard
          index={7}
          title="Campaign Context"
          subtitle="Optional — tune content for an active campaign."
          icon="📣"
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
              gap: 2.5,
            }}
          >
            {renderField(
              "Campaign Name",
              "campaignContext.currentCampaignName",
              false,
              1,
              MAX_CHARS,
              false,
            )}
            <Box>
              <FieldLabel required={false}>Emotional Theme</FieldLabel>
              <StyledSelect
                value={
                  brandProfile.campaignContext?.primaryEmotionalTheme || ""
                }
                onChange={(e) =>
                  setNestedField("campaignContext", {
                    ...(brandProfile.campaignContext || {}),
                    primaryEmotionalTheme: e.target.value,
                  })
                }
                displayEmpty
              >
                <MenuItem value="">
                  <Typography sx={{ color: T.muted }}>— None —</Typography>
                </MenuItem>
                {emotionalThemeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </StyledSelect>
            </Box>
            <Box>
              <FieldLabel required={false}>Seasonal Context</FieldLabel>
              <StyledSelect
                value={brandProfile.campaignContext?.seasonalContext || ""}
                onChange={(e) =>
                  setNestedField("campaignContext", {
                    ...(brandProfile.campaignContext || {}),
                    seasonalContext: e.target.value,
                  })
                }
                displayEmpty
              >
                <MenuItem value="">
                  <Typography sx={{ color: T.muted }}>— None —</Typography>
                </MenuItem>
                {seasonalContextOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </StyledSelect>
            </Box>
          </Box>
        </SectionCard>

        {/* SECTION 8 header */}
        <Box
          sx={{
            background: T.gradTop,
            borderRadius: "20px",
            p: { xs: 3, md: 4 },
            mb: 3,
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(ellipse at 20% 80%, rgba(91,91,246,0.25) 0%, transparent 60%)",
              pointerEvents: "none",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: fontMono,
              fontSize: "11px",
              color: "rgba(255,255,255,0.4)",
              fontWeight: 500,
              letterSpacing: "0.1em",
              mb: 1,
            }}
          >
            SECTION 08
          </Typography>
          <Typography
            sx={{
              fontFamily: fontDisplay,
              fontWeight: 700,
              fontSize: "22px",
              color: T.white,
              mb: 1,
            }}
          >
            Brand Strategy & AI Intelligence
          </Typography>
          <Typography
            sx={{
              fontSize: "13.5px",
              color: "rgba(255,255,255,0.55)",
              lineHeight: 1.7,
              maxWidth: 560,
            }}
          >
            These 12 questions power your content agents' hooks, CTAs, voice
            calibration, and contrarian angles.
          </Typography>
        </Box>

        <SectionCard
          index="8A"
          title="Required Strategy Fields"
          subtitle="Every account needs these to generate on-brand content."
          icon="🔒"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {renderStrategicField(
              "Q01 🎯 One-Sentence Brand Promise",
              "brandPromise",
              "The specific, measurable commitment you make to every customer.",
              2,
              200,
              true,
            )}
            {renderStrategicField(
              "Q02 😈 The Brand Enemy",
              "brandEnemy",
              "A mindset or industry norm your brand exists to fight.",
              3,
              BIG_MAX_CHARS,
              true,
            )}
            {renderStrategicField(
              "Q03 🧠 Core Beliefs Nobody Else Says",
              "coreBeliefs",
              "3–5 things you deeply believe about your industry.",
              5,
              600,
              true,
            )}
          </Box>
        </SectionCard>

        <SectionCard
          index="8B"
          title="Optional Strategy Fields"
          subtitle="Fill in once you have data, customers, and traction."
          icon="🔓"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <OptionalAccordion
              title="Q04 — Voice Reference"
              badge="voice calibration"
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: T.muted,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                Paste 2–3 sentences from past content that felt exactly right.
              </Typography>
              {renderStrategicField(
                "Voice Reference Examples",
                "voiceReferenceExamples",
                null,
                5,
                VOICE_MAX_CHARS,
                false,
              )}
            </OptionalAccordion>
            <OptionalAccordion
              title="Q05 — Exact Audience Language"
              badge="hook generation"
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: T.muted,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                10–15 real phrases your ideal customer actually says.
              </Typography>
              {renderStrategicField(
                "Audience Language",
                "exactAudienceLanguage",
                null,
                6,
                VOICE_MAX_CHARS,
                false,
              )}
            </OptionalAccordion>
            <OptionalAccordion
              title="Q06 — Signature Phrases"
              badge="voice calibration"
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: T.muted,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                10–15 expressions or vocabulary words this brand uses
                repeatedly.
              </Typography>
              {renderStrategicField(
                "Signature Phrases",
                "signaturePhrases",
                null,
                5,
                VOICE_MAX_CHARS,
                false,
              )}
            </OptionalAccordion>
            <OptionalAccordion
              title="Q07 — Audience's Top 5 Fears"
              badge="emotional hooks"
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: T.muted,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                The 5 things your ideal customer is most afraid of.
              </Typography>
              {renderStrategicField(
                "Audience Fears",
                "audienceFears",
                null,
                4,
                600,
                false,
              )}
            </OptionalAccordion>
            <OptionalAccordion
              title="Q08 — The Transformation Story"
              badge="storytelling"
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: T.muted,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                One specific customer: worst moment → best outcome because of
                your brand.
              </Typography>
              {renderStrategicField(
                "Transformation Story",
                "customerTransformationStory",
                null,
                6,
                VOICE_MAX_CHARS,
                false,
              )}
            </OptionalAccordion>
            <OptionalAccordion
              title="Q09 — Social Proof Numbers"
              badge="authority content"
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: T.muted,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                Your best credibility numbers — customers served, results,
                metrics.
              </Typography>
              {renderStrategicField(
                "Social Proof Data",
                "socialProofData",
                null,
                3,
                BIG_MAX_CHARS,
                false,
              )}
            </OptionalAccordion>
            <OptionalAccordion
              title="Q10 — Primary CTA Per Platform"
              badge="conversion"
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: T.muted,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                The ONE action you want someone to take on each platform. e.g.
                "Instagram: DM 'START'. LinkedIn: Book a demo."
              </Typography>
              {renderStrategicField(
                "Primary CTA Per Platform",
                "primaryCtaPerPlatform",
                null,
                5,
                600,
                false,
              )}
            </OptionalAccordion>

            <OptionalAccordion
              title="Q11 — Funnel Stage Focus"
              badge="CTA intensity"
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: T.muted,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                Where is most of your audience in their buying journey?
              </Typography>
              <Box sx={{ mb: 2 }}>
                <FieldLabel required={false}>Primary Funnel Stage</FieldLabel>
                <StyledSelect
                  value={brandProfile.funnelStageFocus || ""}
                  onChange={(e) =>
                    setNestedField("funnelStageFocus", e.target.value)
                  }
                  displayEmpty
                >
                  <MenuItem value="">
                    <Typography sx={{ color: T.muted }}>Select…</Typography>
                  </MenuItem>
                  {funnelStageOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </Box>
              <Box>
                <FieldLabel
                  required={false}
                  tooltip="e.g. 60% cold, 30% warm, 10% hot"
                >
                  Rough % Breakdown
                </FieldLabel>
                <TextField
                  fullWidth
                  placeholder="e.g. 60% cold, 30% warm, 10% hot"
                  value={brandProfile.funnelStageBreakdown || ""}
                  inputProps={{ maxLength: 150 }}
                  onChange={(e) =>
                    setNestedField("funnelStageBreakdown", e.target.value)
                  }
                  helperText={`${(brandProfile.funnelStageBreakdown || "").length} / 150`}
                />
              </Box>
            </OptionalAccordion>
            <OptionalAccordion
              title="Q12 — Hard Guardrails"
              badge="brand safety"
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: T.muted,
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                Claims your brand can NEVER make — legally or ethically. e.g.
                "No guaranteed ROI claims."
              </Typography>
              {renderStrategicField(
                "Hard Guardrails",
                "claimsNotAllowed",
                null,
                4,
                600,
                false,
              )}
            </OptionalAccordion>
          </Box>
        </SectionCard>

        {/* SUBMIT */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mt: 2,
          }}
        >
          <Typography sx={{ fontSize: "13px", color: T.muted }}>
            {progress < 100
              ? `${100 - progress}% left to complete your profile`
              : "Profile looking complete! 🎉"}
          </Typography>
          <Button
            variant="contained"
            onClick={handleCheckout}
            sx={{
              background: `linear-gradient(135deg, ${T.accent} 0%, #7B5BF6 100%)`,
              borderRadius: "12px",
              px: 4,
              py: 1.5,
              fontSize: "14px",
              fontWeight: 700,
              fontFamily: fontBody,
              textTransform: "none",
              boxShadow: `0 4px 20px ${T.accentGlow}`,
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 8px 32px rgba(91,91,246,0.35)",
                background: `linear-gradient(135deg, #4A4AE8 0%, #6B4AE8 100%)`,
              },
            }}
          >
            Proceed to Checkout →
          </Button>
        </Box>
      </Box>
    </>
  );
}
